import {
    ContributionFilterRegistry,
    FilterContribution
} from '@theia/core/lib/common/contribution-filter/contribution-filter';
import { injectable, type interfaces } from '@theia/core/shared/inversify';
import { CommandContribution, MenuContribution } from '@theia/core/lib/common';
import { KeybindingContribution } from '@theia/core/lib/browser';
import { QuickAccessContribution } from '@theia/core/lib/browser/quick-input/quick-access';
import { CallHierarchyContribution } from '@theia/callhierarchy/lib/browser/callhierarchy-contribution';
import { ProblemContribution } from '@theia/markers/lib/browser/problem/problem-contribution';
import { OutlineViewContribution } from '@theia/outline-view/lib/browser/outline-view-contribution';
import { PreferencesContribution } from '@theia/preferences/lib/browser/preferences-contribution';
import { TaskFrontendContribution } from '@theia/task/lib/browser/task-frontend-contribution';
import { TestOutputViewContribution } from '@theia/test/lib/browser/view/test-output-view-contribution';
import { TestResultViewContribution } from '@theia/test/lib/browser/view/test-result-view-contribution';
import { TestRunViewContribution } from '@theia/test/lib/browser/view/test-run-view-contribution';
import { TestViewContribution } from '@theia/test/lib/browser/view/test-view-contribution';
import { TypeHierarchyContribution } from '@theia/typehierarchy/lib/browser/typehierarchy-contribution';

/** Widget IDs closed on startup when restored from a saved layout. */
export const FILTERED_BUILTIN_WIDGET_IDS = [
    'callhierarchy',
    'test-view-container',
    'test-tree-widget',
    'test-run-widget',
    'test-result-widget',
    'test-output-view',
    'theia-typehierarchy'
] as const;

type ContributionConstructor = abstract new (...args: never[]) => object;

const FILTERED_CONTRIBUTION_CONSTRUCTORS: readonly ContributionConstructor[] = [
    CallHierarchyContribution,
    OutlineViewContribution,
    ProblemContribution,
    TestOutputViewContribution,
    TestResultViewContribution,
    TestRunViewContribution,
    TestViewContribution,
    TypeHierarchyContribution
];

/** Block stock preferences commands/menus; keybinding stays on PreferencesContribution. */
const PREFERENCES_UI_CONTRIBUTION_IDENTIFIERS: readonly interfaces.ServiceIdentifier<object>[] = [
    CommandContribution,
    MenuContribution
];

/**
 * `TaskFrontendContribution` must NOT be filtered from `'*'`: its
 * `FrontendApplicationContribution.onStart()` is the only place Theia registers
 * the task resolvers, which expand `${workspaceFolder}`/`${cwd}` and default a
 * task's cwd to the workspace root. Filtering it everywhere left tasks
 * unresolved (`${workspaceFolder}` reached the shell and bash expanded it to "")
 * and spawned terminals in the app's launch dir. We only hide its UI, by
 * filtering it from these UI-only identifiers (the other identifiers it binds).
 */
const TASK_UI_CONTRIBUTION_IDENTIFIERS: readonly interfaces.ServiceIdentifier<object>[] = [
    CommandContribution,
    MenuContribution,
    KeybindingContribution,
    QuickAccessContribution
];

const isFilteredContribution = (contribution: unknown): boolean => {
    if (typeof contribution !== 'object' || contribution === null) return false;

    return FILTERED_CONTRIBUTION_CONSTRUCTORS.some(
        (contributionConstructor: ContributionConstructor): boolean => contribution instanceof contributionConstructor
    );
};

@injectable()
export class ContributionFilters implements FilterContribution {
    registerContributionFilters(registry: ContributionFilterRegistry): void {
        registry.addFilters('*', [(contribution: unknown): boolean => !isFilteredContribution(contribution)]);
        registry.addFilters(
            [...TASK_UI_CONTRIBUTION_IDENTIFIERS],
            [(contribution: unknown): boolean => !(contribution instanceof TaskFrontendContribution)]
        );
        registry.addFilters(
            [...PREFERENCES_UI_CONTRIBUTION_IDENTIFIERS],
            [(contribution: unknown): boolean => !(contribution instanceof PreferencesContribution)]
        );
    }
}

export function registerContributionFilters({ bind }: { bind: interfaces.Bind }): void {
    bind(ContributionFilters).toSelf().inSingletonScope();
    bind(FilterContribution).toService(ContributionFilters);
}
